// hooks/useMenuData.js
import { useState, useEffect, useCallback } from 'react';

import { filterAndSortCategories } from '../utils/categoryHelpers';
import {
  API_ENDPOINT,
  API_HEADERS,
  CACHE_DURATION,
  GET_ALL_CATEGORIES_WITH_DETAILS,
  GET_ALL_COLLECTIONS
} from '../utils/constants';

// Cache to prevent refetching on remounts
const cache = {
  categories: null,
  collections: null,
  storeStatus: null,
  timestamp: null
};

export const useMenuData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [storeStatus, setStoreStatus] = useState(null);

  const fetchData = useCallback(async () => {
    // Check cache first
    const now = Date.now();
    if (cache.timestamp && now - cache.timestamp < CACHE_DURATION) {
      setCategories(cache.categories);
      setCollections(cache.collections);
      setStoreStatus(cache.storeStatus);
      setLoading(false);
      return;
    }

    try {
      // Parallel requests for better performance
      const [categoriesResponse, collectionsResponse] = await Promise.all([
        fetch(API_ENDPOINT, {
          method: 'POST',
          headers: API_HEADERS,
          body: JSON.stringify({ query: GET_ALL_CATEGORIES_WITH_DETAILS })
        }),
        fetch(API_ENDPOINT, {
          method: 'POST',
          headers: API_HEADERS,
          body: JSON.stringify({ query: GET_ALL_COLLECTIONS })
        })
      ]);

      // Check if responses are ok
      if (!categoriesResponse.ok || !collectionsResponse.ok) {
        throw new Error('Failed to fetch menu data');
      }

      const [categoriesResult, collectionsResult] = await Promise.all([
        categoriesResponse.json(),
        collectionsResponse.json()
      ]);

      // Handle GraphQL errors
      if (categoriesResult.errors) {
        throw new Error(categoriesResult.errors[0].message);
      }

      if (collectionsResult.errors) {
        throw new Error(collectionsResult.errors[0].message);
      }

      // Process categories
      const sortedCategories = filterAndSortCategories(
        categoriesResult.data.categories.items
      );

      const newStoreStatus = collectionsResult?.data?.setting?.storeStatus;
      const newCollections = collectionsResult?.data?.collections?.items || [];

      // Update cache
      cache.categories = sortedCategories;
      cache.collections = newCollections;
      cache.storeStatus = newStoreStatus;
      cache.timestamp = now;

      setCategories(sortedCategories);
      setCollections(newCollections);
      setStoreStatus(newStoreStatus);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clear cache function (useful for force refresh)
  const clearCache = useCallback(() => {
    cache.categories = null;
    cache.collections = null;
    cache.storeStatus = null;
    cache.timestamp = null;
  }, []);

  return {
    loading,
    error,
    categories,
    collections,
    storeStatus,
    refetch: fetchData,
    clearCache
  };
};
